import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AssignInstructorToProgramOptions,
  ChangeProgramInstructorsApplicationErrors,
  ChangeProgramInstructorsOptions,
  CreateInstructorOptions,
  DeleteInstructorOptions,
  DeleteInstructorResponse,
  Instructor,
  InstructorQuery,
  InstructorsQueryBuilder,
  ListInstructorsOptions,
  ListInstructorsResponse,
  QueryInstructorsResponse,
  UnassignInstructorFromProgramOptions,
  UpdateInstructor,
  UpdateInstructorApplicationErrors,
  UpdateInstructorOptions,
  assignInstructorToProgram as universalAssignInstructorToProgram,
  changeProgramInstructors as universalChangeProgramInstructors,
  createInstructor as universalCreateInstructor,
  deleteInstructor as universalDeleteInstructor,
  inviteInstructor as universalInviteInstructor,
  listInstructors as universalListInstructors,
  queryInstructors as universalQueryInstructors,
  typedQueryInstructors as universalTypedQueryInstructors,
  unassignInstructorFromProgram as universalUnassignInstructorFromProgram,
  updateInstructor as universalUpdateInstructor,
} from './achievements-instructors-v2-instructor-instructors.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/online-programs' };

export function createInstructor(
  httpClient: HttpClient
): CreateInstructorSignature {
  return (
    options?: NonNullablePaths<CreateInstructorOptions, `instructor.name`, 3>
  ) =>
    universalCreateInstructor(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateInstructorSignature {
  /**
   * Creates a new instructor
   * @returns The created instructor
   */
  (
    options?: NonNullablePaths<CreateInstructorOptions, `instructor.name`, 3>
  ): Promise<
    NonNullablePaths<
      Instructor,
      | `photo._id`
      | `photo.url`
      | `photo.height`
      | `photo.width`
      | `role`
      | `programIds`,
      3
    >
  >;
}

export function inviteInstructor(
  httpClient: HttpClient
): InviteInstructorSignature {
  return (email: string) =>
    universalInviteInstructor(
      email,
      // @ts-ignore
      { httpClient }
    );
}

interface InviteInstructorSignature {
  /**
   * Invite instructor by provided email
   * @param - Instructor's email to invite
   */
  (email: string): Promise<void>;
}

export function listInstructors(
  httpClient: HttpClient
): ListInstructorsSignature {
  return (options?: ListInstructorsOptions) =>
    universalListInstructors(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListInstructorsSignature {
  /**
   * Lists instructors created on the site
   */
  (options?: ListInstructorsOptions): Promise<
    NonNullablePaths<
      ListInstructorsResponse,
      | `instructors`
      | `instructors.${number}.photo._id`
      | `instructors.${number}.photo.url`
      | `instructors.${number}.photo.height`
      | `instructors.${number}.photo.width`
      | `instructors.${number}.role`,
      5
    >
  >;
}

export function queryInstructors(
  httpClient: HttpClient
): QueryInstructorsSignature {
  return () =>
    universalQueryInstructors(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryInstructorsSignature {
  /**
   * Query instructors
   */
  (): InstructorsQueryBuilder;
}

export function typedQueryInstructors(
  httpClient: HttpClient
): TypedQueryInstructorsSignature {
  return (query: InstructorQuery) =>
    universalTypedQueryInstructors(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryInstructorsSignature {
  /** */
  (query: InstructorQuery): Promise<
    NonNullablePaths<
      QueryInstructorsResponse,
      | `instructors`
      | `instructors.${number}.photo._id`
      | `instructors.${number}.photo.url`
      | `instructors.${number}.photo.height`
      | `instructors.${number}.photo.width`
      | `instructors.${number}.role`,
      5
    >
  >;
}

export function updateInstructor(
  httpClient: HttpClient
): UpdateInstructorSignature {
  return (
    _id: string,
    instructor: UpdateInstructor,
    options?: UpdateInstructorOptions
  ) =>
    universalUpdateInstructor(
      _id,
      instructor,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateInstructorSignature {
  /**
   * Updates an instructor, supports partial update
   * @param - ID of the instructor that equals to memberId
   * @returns Updated instructor
   */
  (
    _id: string,
    instructor: UpdateInstructor,
    options?: UpdateInstructorOptions
  ): Promise<
    NonNullablePaths<
      Instructor,
      | `photo._id`
      | `photo.url`
      | `photo.height`
      | `photo.width`
      | `role`
      | `programIds`,
      3
    > & {
      __applicationErrorsType?: UpdateInstructorApplicationErrors;
    }
  >;
}

export function deleteInstructor(
  httpClient: HttpClient
): DeleteInstructorSignature {
  return (instructorId: string, options?: DeleteInstructorOptions) =>
    universalDeleteInstructor(
      instructorId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteInstructorSignature {
  /**
   * Deletes an instructor
   * @param - ID of the instructor to delete
   */
  (instructorId: string, options?: DeleteInstructorOptions): Promise<
    NonNullablePaths<
      DeleteInstructorResponse,
      | `instructor.photo._id`
      | `instructor.photo.url`
      | `instructor.photo.height`
      | `instructor.photo.width`
      | `instructor.role`
      | `instructor.programIds`,
      4
    >
  >;
}

export function assignInstructorToProgram(
  httpClient: HttpClient
): AssignInstructorToProgramSignature {
  return (
    instructorId: string,
    programId: string,
    options?: AssignInstructorToProgramOptions
  ) =>
    universalAssignInstructorToProgram(
      instructorId,
      programId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignInstructorToProgramSignature {
  /**
   * Assigns an instructor to the program
   * @param - ID of the instructor to assign
   * @param - ID of the program
   */
  (
    instructorId: string,
    programId: string,
    options?: AssignInstructorToProgramOptions
  ): Promise<void>;
}

export function unassignInstructorFromProgram(
  httpClient: HttpClient
): UnassignInstructorFromProgramSignature {
  return (
    instructorId: string,
    programId: string,
    options?: UnassignInstructorFromProgramOptions
  ) =>
    universalUnassignInstructorFromProgram(
      instructorId,
      programId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UnassignInstructorFromProgramSignature {
  /**
   * Unassigns an instructor from the program
   * @param - ID of the instructor to unassign
   * @param - ID of the program
   */
  (
    instructorId: string,
    programId: string,
    options?: UnassignInstructorFromProgramOptions
  ): Promise<void>;
}

export function changeProgramInstructors(
  httpClient: HttpClient
): ChangeProgramInstructorsSignature {
  return (programId: string, options?: ChangeProgramInstructorsOptions) =>
    universalChangeProgramInstructors(
      programId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeProgramInstructorsSignature {
  /**
   * Assigns or unassigns instructors to the program
   * @param - ID of the program
   */
  (programId: string, options?: ChangeProgramInstructorsOptions): Promise<
    void & {
      __applicationErrorsType?: ChangeProgramInstructorsApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  Asset,
  AssignInstructorToProgramOptions,
  AssignInstructorToProgramRequest,
  AssignInstructorToProgramResponse,
  ChangeProgramInstructorsOptions,
  ChangeProgramInstructorsRequest,
  ChangeProgramInstructorsResponse,
  CreateInstructorOptions,
  CreateInstructorRequest,
  CreateInstructorResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteContext,
  DeleteInstructorOptions,
  DeleteInstructorRequest,
  DeleteInstructorResponse,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  Instructor,
  InstructorQuerySpec,
  InstructorWasAssigned,
  InstructorWasUnassigned,
  InstructorsQueryBuilder,
  InstructorsQueryResult,
  InviteInstructorRequest,
  InviteInstructorResponse,
  ListInstructorsOptions,
  ListInstructorsRequest,
  ListInstructorsResponse,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  NoUserIdInCallScopeErrorDetails,
  OdeditorAssigned,
  OdeditorUnassigned,
  Paging,
  PagingMetadataV2,
  PicassoAssigned,
  PicassoUnassigned,
  PolicyAssignedToContributor,
  PolicyRemovedFromContributor,
  PolicyUpdatedForContributor,
  QueryInstructorsRequest,
  QueryInstructorsResponse,
  RestoreInfo,
  Role,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  UnassignInstructorFromProgramOptions,
  UnassignInstructorFromProgramRequest,
  UnassignInstructorFromProgramResponse,
  UpdateInstructor,
  UpdateInstructorOptions,
  UpdateInstructorRequest,
  UpdateInstructorResponse,
  UpdateInstructorRoleRequest,
  UpdateInstructorRoleResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './achievements-instructors-v2-instructor-instructors.universal.js';
